import * as React from "react";
import { Pressable, Text, View, type PressableProps } from "react-native";
import Animated from "react-native-reanimated";
import { useCSSVariable } from "uniwind";
import { cn } from "@/shared/lib/utils";
import { usePressableScale } from "@/shared/hooks/use-pressable-scale";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

type Variant = "solid" | "soft" | "outline" | "ghost";
type Tone = "neutral" | "brand" | "pink";

type PillProps = Omit<PressableProps, "children"> & {
  label: string;
  variant?: Variant;
  tone?: Tone;
  selected?: boolean;
  size?: "sm" | "md";
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  badge?: number;
  minWidth?: number;
  className?: string;
};

function getPillClasses(
  variant: Variant,
  tone: Tone,
  selected: boolean
): { container: string; text: string } {
  if (variant === "solid") {
    const base =
      tone === "brand" ? "bg-brand" : tone === "pink" ? "bg-pink" : "bg-accent";
    const text =
      tone === "brand"
        ? "text-primary-foreground"
        : tone === "pink"
          ? "text-white"
          : "text-foreground";
    const ring = selected
      ? tone === "brand"
        ? "ring-2 ring-brand/40"
        : tone === "pink"
          ? "ring-2 ring-pink/40"
          : "ring-2 ring-accent/60"
      : "";
    return { container: cn(base, ring), text };
  }

  if (variant === "outline") {
    if (selected) {
      const container =
        tone === "brand"
          ? "border border-brand bg-brand/10"
          : tone === "pink"
            ? "border border-pink bg-pink/10"
            : "border border-foreground";
      const text =
        tone === "brand"
          ? "text-brand"
          : tone === "pink"
            ? "text-pink"
            : "text-foreground";
      return { container, text };
    }
    return { container: "border border-border", text: "text-foreground" };
  }

  if (variant === "ghost") {
    return {
      container: selected ? "bg-accent" : "",
      text: "text-foreground",
    };
  }

  // soft (default)
  if (selected) {
    const container =
      tone === "brand"
        ? "bg-brand/15"
        : tone === "pink"
          ? "bg-pink/15"
          : "bg-accent";
    const text =
      tone === "brand"
        ? "text-brand"
        : tone === "pink"
          ? "text-pink"
          : "text-foreground";
    return { container, text };
  }
  return { container: "", text: "text-foreground" };
}

export function Pill({
  label,
  variant = "soft",
  tone = "brand",
  selected = false,
  size = "md",
  leftIcon,
  rightIcon,
  badge,
  minWidth,
  className,
  disabled,
  onPress,
  onPressIn: callerPressIn,
  onPressOut: callerPressOut,
  ...props
}: PillProps) {
  const { onPressIn, onPressOut, animatedStyle } = usePressableScale(0.95);
  const [surfaceColor] = useCSSVariable(["--color-surface"]) as [string];

  const { container: containerClass, text: textClass } = getPillClasses(
    variant,
    tone,
    selected
  );

  const softBg =
    variant === "soft" && !selected
      ? { backgroundColor: surfaceColor }
      : undefined;

  const paddingClass = size === "sm" ? "px-3 py-1.5" : "px-4 py-2";
  const textSize =
    size === "sm" ? "text-xs font-medium" : "text-sm font-medium";

  return (
    <AnimatedPressable
      onPress={(e) => {
        onPress?.(e);
      }}
      onPressIn={(e) => {
        if (!disabled) onPressIn();
        callerPressIn?.(e);
      }}
      onPressOut={(e) => {
        if (!disabled) onPressOut();
        callerPressOut?.(e);
      }}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityState={{ selected, disabled: !!disabled }}
      className={cn(
        "flex-row items-center justify-center gap-2 rounded-full",
        paddingClass,
        containerClass,
        disabled && "opacity-50",
        className
      )}
      style={[animatedStyle, softBg, minWidth ? { minWidth } : undefined]}
      {...props}
    >
      {leftIcon}
      <Text className={cn(textClass, textSize)}>{label}</Text>
      {rightIcon}
      {!!badge && badge > 0 && (
        <View
          className="bg-pink absolute -top-1 -right-1 h-[18px] min-w-[18px] items-center justify-center rounded-full px-1"
          accessibilityElementsHidden
          importantForAccessibility="no"
        >
          <Text className="text-[10px] font-semibold text-white">
            {badge > 99 ? "99+" : badge}
          </Text>
        </View>
      )}
    </AnimatedPressable>
  );
}
